import React, { FunctionComponent } from 'react';
import { string, SchemaOf, object } from 'yup';
import {
  MEDIUM_PLAIN_TEXT_LENGTH,
  MEDIUM_PLAIN_TEXT_VALIDATION_MESSAGE,
  REQUIRED_ERROR_MESSAGE,
  SHORT_PLAIN_TEXT_LENGTH,
  SHORT_PLAIN_TEXT_VALIDATION_MESSAGE
} from '../../../constans/common-constants';
import { useStore } from '../../../store/store-provider';
import { observer } from 'mobx-react';
import { FormikWrapper, IFormField } from '../../shared/formik-wrapper/formik-wrapper';
import ChatInputWithButton from '../chat-input-with-button/chat-input-with-button';
import ChatInput from '../chat-input/chat-input';
import { FormikValues } from 'formik';
import { DESKTOP_BREAKPOINT } from '../../../constans/responsive-break-points';
import ChatResizeContainer from '../chat-resize-container/chat-resize-container';
import ChatMessagesContainer from '../chat-messages-container/chat-messages-container';
import './chat-popup.scss';
import { getMixpanelService } from '../../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../../constans/mixpanel-contants';

interface ChatInputSchema {
  name: string;
  message: string;
}

const inputsSchema: SchemaOf<ChatInputSchema> = object().shape({
  name: string()
    .trim()
    .max(SHORT_PLAIN_TEXT_LENGTH, SHORT_PLAIN_TEXT_VALIDATION_MESSAGE)
    .required(`Name ${REQUIRED_ERROR_MESSAGE}`),
  message: string()
    .trim()
    .max(MEDIUM_PLAIN_TEXT_LENGTH, MEDIUM_PLAIN_TEXT_VALIDATION_MESSAGE)
    .required(`Message ${REQUIRED_ERROR_MESSAGE}`),
});

const inputFields: IFormField[] = [
  { fieldName: 'name', value: 'Name', placeholder: 'Enter your full name', component: ChatInput, type: 'text', autocomplete: 'off' },
  { fieldName: 'message', value: 'Message', placeholder: 'Message…', component: ChatInputWithButton, type: 'text', autocomplete: 'off' },
];

const ChatPopup: FunctionComponent = (): JSX.Element => {
  const { chatStore, podiumInfoStore } = useStore();
  const { isResolutionBreakPointActive, windowResolution } = podiumInfoStore;
  const {
    chatMessagesList,
    setUserNameHandel,
    setMessageTextHandel,
    sendMessageHandler,
    userName,
    isMyMessage,
    clearDataAboutLastUserMessage,
    getPaginateChatMessages,
    toggleChatBoxVisibility,
    defineNewMessageIndicatorVisibilityState,
    resetUnreadMessagesNumber,
    incrementUnreadMessagesNumber,
    isVisibleNewMessageIndicator,
    isVisible
  } = chatStore;
  const isMobile = isResolutionBreakPointActive(DESKTOP_BREAKPOINT);

  const initialValues: ChatInputSchema = {
    name: userName,
    message: ''
  };

  const sendMessage = (formData: ChatInputSchema, { setFieldValue }: FormikValues): void => {
    setUserNameHandel(formData.name);
    setMessageTextHandel(formData.message);
    sendMessageHandler();
    setFieldValue('message', '', false);
    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.MESSAGES, MIXPANEL_EVENT_TYPES.SENT);
  };

  return (
    <ChatResizeContainer
      isVisible={isVisible}
      resolution={windowResolution}
      isMobile={isMobile}
      onClose={toggleChatBoxVisibility}
      messagesCount={chatMessagesList.length}
    >
      <>
        <ChatMessagesContainer
          isVisible={isVisible}
          userName={userName}
          messages={chatMessagesList}
          clearUserLastMessageData={clearDataAboutLastUserMessage}
          isMyMessage={isMyMessage}
          paginationHandler={getPaginateChatMessages}
          isVisibleNewMessageIndicator={isVisibleNewMessageIndicator}
          defineNewMessageIndicatorVisibilityState={defineNewMessageIndicatorVisibilityState}
          resetUnreadMessagesNumber={resetUnreadMessagesNumber}
          incrementUnreadMessagesNumber={incrementUnreadMessagesNumber}
        />
        <FormikWrapper
          rootClassname="chat-box-footer"
          enableReinitialize
          initialValues={initialValues}
          inputsSchema={inputsSchema}
          inputFields={inputFields}
          handleSubmit={sendMessage}
        />
      </>
    </ChatResizeContainer>
  );
};

export default observer(ChatPopup);
